import React from 'react';
import { VirtualizedFormSelect } from '@roger/ui-components';
import {
  cliniciansToSelectOptions,
  type ClinicianRole,
  type ClinicianSelectInput,
} from '../../utils/clinicianUtils';

type VirtualizedFormSelectProps = React.ComponentProps<
  typeof VirtualizedFormSelect
>;

export interface ClinicianSelectProps
  extends Omit<VirtualizedFormSelectProps, 'options'> {
  /** Raw available-clinician (UserClinic) records to choose from. */
  clinicians: ClinicianSelectInput[];
  /** Restrict options to a single assignable role. Omit to include everyone. */
  role?: ClinicianRole;
  /** Override an option's searchable label while keeping shared option building. */
  optionLabelFormatter?: (
    clinician: ClinicianSelectInput,
    defaultLabel: string
  ) => string;
}

/**
 * Reusable clinician / case-manager / approver picker.
 *
 * Wraps {@link VirtualizedFormSelect} with the shared option builder so labels
 * are formatted consistently (linked clinician → user → email → username) and
 * records with no usable name are dropped instead of rendering as a blank row
 * (ROG-2698). Prefer this over hand-rolling `clinicians.map(...)` at call sites.
 *
 * Integrates with react-hook-form via the usual `{...register('field')}` spread,
 * or use it controlled with `defaultValue` + `onChange`.
 *
 * @example
 * <ClinicianSelect
 *   label="Case Manager"
 *   placeholder="Select case manager..."
 *   clinicians={availableClinicians}
 *   role="caseManager"
 *   required
 *   error={errors.case_manager_id?.message}
 *   {...register('case_manager_id', { onBlur: handleFieldBlur })}
 * />
 */
export const ClinicianSelect = React.forwardRef<
  HTMLInputElement,
  ClinicianSelectProps
>(
  (
    { clinicians, role, optionLabelFormatter, sortOptions = true, ...rest },
    ref
  ) => {
    // Memoize so the options array keeps a stable identity across renders (the
    // underlying select re-sorts when it changes).
    const options = React.useMemo(() => {
      const baseOptions = cliniciansToSelectOptions(clinicians, role);
      if (!optionLabelFormatter) return baseOptions;

      const cliniciansById = new Map(clinicians.map(c => [c.id, c]));
      return baseOptions.map(option => {
        const clinician = cliniciansById.get(option.value);
        if (!clinician) return option;
        const label = optionLabelFormatter(clinician, option.label).trim();
        return label ? { ...option, label } : option;
      });
    }, [clinicians, optionLabelFormatter, role]);
    return (
      <VirtualizedFormSelect
        ref={ref}
        options={options}
        sortOptions={sortOptions}
        {...rest}
      />
    );
  }
);

ClinicianSelect.displayName = 'ClinicianSelect';
